/* global login, license, window, ifCl */
import React, { createContext, useContext, useEffect, useState } from "react";
import { GeminiClient } from "./GeminiClient";
import ProfileContext from "common/ProfileContext";
import {
  atBqnTarget,
  parseSystemId,
  parseVersion,
  retrieveWebParameters,
} from "common/api";
import BqnContext from "common/BqnContext";
import { CHATBOT_ACTION } from "./CHATBOT_ACTION";

const GeminiContext = createContext({
  geminiClient: undefined,
  open: false,
  setOpen: () => {},
  action: undefined,
  sendAction: () => {},
});

export function useGeminiClient() {
  const context = useContext(GeminiContext);
  return context.geminiClient;
}

export const GeminiContextProvider = ({ children }) => {
  const [geminiClient, setGeminiClient] = useState();
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState();

  const profile = useContext(ProfileContext);
  const { clusterStatus, loginBqnId } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  useEffect(() => {
    if (
      !profile ||
      loginBqnId === undefined ||
      mode === undefined ||
      (mode === "cluster" && loginBqnId === undefined)
    ) {
      return;
    }

    initialize();
    // The BQN selected in the header is not one of the BQNs asked for, so the
    // chatbot is not created again when it changes.
  }, [profile, loginBqnId, mode]);

  // The chatbot is given no system ID when the BQN reports none.
  const systemIdGiven = (response) => parseSystemId(response) ?? null;

  function parseGeminiUrlResponse(response) {
    const value = response.match(
      /Chatbot URL: (.*?)\r?\nChatbot secret: (.*?)\r?\n/,
    );
    if (!value) {
      return null;
    }

    return {
      url: value[1],
      secret: value[2],
    };
  }

  function initialize() {
    // The license is the primary's, the release the entry node's.
    const bqnIdLicensed = loginBqnId;

    Promise.all([
      ifCl
        .run(atBqnTarget("show hardware id", mode, bqnIdLicensed))
        .then(systemIdGiven),
      retrieveWebParameters().then(parseGeminiUrlResponse),
      ifCl
        .run(atBqnTarget("show version", mode, loginBqnId))
        .then(parseVersion),
    ]).then(([hardwareId, parameters, version]) => {
      const { url, secret } = parameters || {};

      // The secret is not reported: it is the credential of the chatbot.
      if (!url || !secret) {
        console.error("The chatbot has no URL or no secret: not created.");

        return;
      }

      setGeminiClient(new GeminiClient(hardwareId, url, secret, version));
    });
  }

  function sendAction(actionArg, prompt) {
    if (!Object.prototype.hasOwnProperty.call(CHATBOT_ACTION, actionArg)) {
      console.warn("Unknown action", actionArg);
      return;
    }

    setAction({ action: actionArg, prompt });
  }

  return (
    <GeminiContext.Provider
      value={{
        geminiClient,
        open,
        setOpen,
        sendAction,
        action,
      }}
    >
      {children}
    </GeminiContext.Provider>
  );
};

export default GeminiContext;
